// frontend/packages/business-console/src/services/schedulingService.js
//
// Client for Scheduling Phase A (D35), the tenant-facing management surface.
// Backend is the AUTHENTICATED management API at /api/v2/scheduling (see
// backend/routes/schedulingRoutes.js) plus the two business-level toggles on
// PATCH /businesses/settings. The tenant is attached automatically by the api
// client's x-business-id interceptor, so we never pass business/slug.
//
// Ship-dark: the whole authed router 404s for any tenant without the
// `scheduling` feature (backend/controllers/schedulingHttp.js
// makeSchedulingFeatureGate). Every reader below therefore treats a 404 as
// "feature off / nothing here" and degrades to an empty result, mirroring
// ryzzecastService.getFacebookStatus. Writes do not swallow errors: the caller
// surfaces err.response.data.message (F.13).
//
// Roles (F.10): config writes + booking management are businessadmin/manager
// only, enforced server-side. We render controls for everyone and let the
// server 403 surface; no client-side role gating.
//
// NOTE (D22 scope cut): there is intentionally NO Console create-booking path
// here. Phase A bookings are minted by the public widget (a later PR); the
// Console only lists bookings and drives their lifecycle.

import { api } from '@upryzze/services';
import { getMyFeatures } from './featuresService';
import { updateBusinessSettings } from './businessService';

// A 404 from the authed scheduling API means the feature is off for this
// tenant (the router-level gate). Return the caller's empty shape instead of
// throwing so the page degrades dark rather than erroring.
function isFeatureOff(err) {
  return err?.response?.status === 404;
}

// ── Feature gate ───────────────────────────────────────────────────────────
// Cheap discovery: is Online booking (scheduling) enabled for this business?
// Reads the generic client feature list (GET /api/v2/features) and finds the
// `scheduling` row. Returns { enabled } and never throws (any error -> off) so
// the page can render a friendly "not available" state.
export async function getSchedulingEnabled() {
  try {
    const features = await getMyFeatures();
    const row = features.find((f) => f.key === 'scheduling');
    return { enabled: Boolean(row?.enabled) };
  } catch {
    return { enabled: false };
  }
}

// ── Staff ────────────────────────────────────────────────────────────────
export async function listStaff() {
  try {
    const { data } = await api.get('/v2/scheduling/staff');
    return Array.isArray(data?.items) ? data.items : [];
  } catch (err) {
    if (isFeatureOff(err)) return [];
    throw err;
  }
}

export async function createStaff(payload) {
  const { data } = await api.post('/v2/scheduling/staff', payload);
  return data?.item;
}

export async function updateStaff(id, patch) {
  const { data } = await api.patch(
    `/v2/scheduling/staff/${encodeURIComponent(id)}`,
    patch
  );
  return data?.item;
}

export async function deleteStaff(id) {
  const { data } = await api.delete(
    `/v2/scheduling/staff/${encodeURIComponent(id)}`
  );
  return data;
}

// ── Schedules (availability rules) ─────────────────────────────────────────
export async function listSchedules(staffId) {
  try {
    const { data } = await api.get('/v2/scheduling/schedules', {
      params: staffId ? { staffId } : undefined,
    });
    return Array.isArray(data?.items) ? data.items : [];
  } catch (err) {
    if (isFeatureOff(err)) return [];
    throw err;
  }
}

export async function createSchedule(payload) {
  const { data } = await api.post('/v2/scheduling/schedules', payload);
  return data?.item;
}

export async function updateSchedule(id, patch) {
  const { data } = await api.patch(
    `/v2/scheduling/schedules/${encodeURIComponent(id)}`,
    patch
  );
  return data?.item;
}

export async function deleteSchedule(id) {
  const { data } = await api.delete(
    `/v2/scheduling/schedules/${encodeURIComponent(id)}`
  );
  return data;
}

// ── Time off ───────────────────────────────────────────────────────────────
export async function listTimeOff(staffId) {
  try {
    const { data } = await api.get('/v2/scheduling/time-off', {
      params: staffId ? { staffId } : undefined,
    });
    return Array.isArray(data?.items) ? data.items : [];
  } catch (err) {
    if (isFeatureOff(err)) return [];
    throw err;
  }
}

export async function createTimeOff(payload) {
  const { data } = await api.post('/v2/scheduling/time-off', payload);
  return data?.item;
}

export async function updateTimeOff(id, patch) {
  const { data } = await api.patch(
    `/v2/scheduling/time-off/${encodeURIComponent(id)}`,
    patch
  );
  return data?.item;
}

export async function deleteTimeOff(id) {
  const { data } = await api.delete(
    `/v2/scheduling/time-off/${encodeURIComponent(id)}`
  );
  return data;
}

// ── Appointment types ──────────────────────────────────────────────────────
export async function listAppointmentTypes() {
  try {
    const { data } = await api.get('/v2/scheduling/appointment-types');
    return Array.isArray(data?.items) ? data.items : [];
  } catch (err) {
    if (isFeatureOff(err)) return [];
    throw err;
  }
}

export async function createAppointmentType(payload) {
  const { data } = await api.post('/v2/scheduling/appointment-types', payload);
  return data?.item;
}

export async function updateAppointmentType(id, patch) {
  const { data } = await api.patch(
    `/v2/scheduling/appointment-types/${encodeURIComponent(id)}`,
    patch
  );
  return data?.item;
}

export async function deleteAppointmentType(id) {
  const { data } = await api.delete(
    `/v2/scheduling/appointment-types/${encodeURIComponent(id)}`
  );
  return data;
}

// ── Bookings (list + lifecycle; NO create per D22) ─────────────────────────
// The list endpoints return a paged shape { docs, total, page, limit, pages }.
// On feature-off (404) we synthesize an empty page so callers need no branch.
const EMPTY_PAGE = { docs: [], total: 0, page: 1, limit: 50, pages: 1 };

export async function listBookings(query = {}) {
  try {
    const { data } = await api.get('/v2/scheduling/bookings', {
      params: query,
    });
    return data || EMPTY_PAGE;
  } catch (err) {
    if (isFeatureOff(err)) return { ...EMPTY_PAGE };
    throw err;
  }
}

export async function listPendingApprovals(query = {}) {
  try {
    const { data } = await api.get('/v2/scheduling/bookings/pending', {
      params: query,
    });
    return data || EMPTY_PAGE;
  } catch (err) {
    if (isFeatureOff(err)) return { ...EMPTY_PAGE };
    throw err;
  }
}

export async function getBooking(id) {
  const { data } = await api.get(
    `/v2/scheduling/bookings/${encodeURIComponent(id)}`
  );
  return data?.booking;
}

export async function confirmBooking(id) {
  const { data } = await api.post(
    `/v2/scheduling/bookings/${encodeURIComponent(id)}/confirm`
  );
  return data?.booking;
}

export async function declineBooking(id) {
  const { data } = await api.post(
    `/v2/scheduling/bookings/${encodeURIComponent(id)}/decline`
  );
  return data?.booking;
}

export async function cancelBooking(id, { reason } = {}) {
  const { data } = await api.post(
    `/v2/scheduling/bookings/${encodeURIComponent(id)}/cancel`,
    { actorType: 'staff', ...(reason ? { reason } : {}) }
  );
  return data?.booking;
}

export async function rescheduleBooking(
  id,
  { startUtc, endUtc, staffId } = {}
) {
  const body = { startUtc };
  if (endUtc) body.endUtc = endUtc;
  if (staffId) body.staffId = staffId;
  const { data } = await api.post(
    `/v2/scheduling/bookings/${encodeURIComponent(id)}/reschedule`,
    body
  );
  return data?.booking;
}

export async function noShowBooking(id) {
  const { data } = await api.post(
    `/v2/scheduling/bookings/${encodeURIComponent(id)}/no-show`
  );
  return data?.booking;
}

export async function completeBooking(id) {
  const { data } = await api.post(
    `/v2/scheduling/bookings/${encodeURIComponent(id)}/complete`
  );
  return data?.booking;
}

// ── Business-level settings (crmSync + defaultBookingMode) ──────────────────
// Read from the loaded business doc (business.settings.scheduling), written via
// the shared whitelist endpoint PATCH /businesses/settings. Only the two keys
// the whitelist accepts are sent.
export function getScheduling(business) {
  return business?.settings?.scheduling || {};
}

export async function saveScheduling(patch) {
  const clean = {};
  if (patch.crmSync !== undefined) clean.crmSync = patch.crmSync;
  if (patch.defaultBookingMode !== undefined) {
    clean.defaultBookingMode = patch.defaultBookingMode;
  }
  return updateBusinessSettings({ scheduling: clean });
}
